import { Fragment, useId, useMemo } from 'react';

import PropTypes from 'prop-types';

import Card from '#globalComponents/Card/Card';
import Pagination from '#globalComponents/Pagination/Pagination';

import ActionScopeBar from './actions/ActionScopeBar';
import ColumnLayoutPicker from './ColumnLayoutPicker';
import SelectAllInFilterBanner, { scopeBannerPropTypes } from './SelectAllInFilterBanner';
import TableHead from './TableHead';
import TableRow from './TableRow';
import { applyFirstColumnWidth, applyGrow, resolveSticky, tableMinWidth } from './tableStyles';
import { useFirstColumnWidth } from './useFirstColumnWidth';
import { useTableLayout } from './useTableLayout';

const StateRow = ({ colSpan, children }) => (
	<tr>
		<td colSpan={colSpan} className="px-4 py-12 text-center text-bgBase-800">
			{children}
		</td>
	</tr>
);

StateRow.propTypes = {
	colSpan: PropTypes.number.isRequired,
	children: PropTypes.node,
};

const Table = ({
	rows,
	columns: allColumns,
	rowId = 'id',
	selection,
	sort,
	pagination,
	expansion,
	onRowClick,
	getRowProps,
	highlightedId,
	isLoading = false,
	emptyLabel = 'Keine Einträge gefunden',
	firstColumnSticky = true,
	actions,
	bulkBar,
	layoutKey,
	onAddColumn,
	addColumnLabel = 'Spalte hinzufügen',
}) => {
	const generatedId = useId();
	const { columns, picker } = useTableLayout(layoutKey, allColumns, firstColumnSticky);
	const getId = typeof rowId === 'function' ? rowId : (row) => row[rowId];
	const colSpan = columns.length + (selection ? 1 : 0);
	const { width: firstColumnWidth, ...firstColumnResize } = useFirstColumnWidth(layoutKey);
	const stickyColumns = useMemo(
		() => applyGrow(applyFirstColumnWidth(resolveSticky(columns, firstColumnSticky), firstColumnWidth)),
		[columns, firstColumnSticky, firstColumnWidth],
	);
	const minWidth = tableMinWidth(stickyColumns, Boolean(selection));
	const scrolls = minWidth > 0;

	return (
		<div className="relative flex min-h-0 flex-col">
			{actions && <ActionScopeBar label={actions.label}>{actions.buttons}</ActionScopeBar>}
			{(pagination || picker) && (
				<div className="flex items-center gap-2">
					<div className="flex-1 min-w-0">
						{pagination && (
							<Pagination
								currentPage={pagination.currentPage}
								totalPages={pagination.totalPages}
								onPageChange={pagination.onPageChange}
								totalItems={pagination.totalItems}
								pageSize={pagination.pageSize}
								showInfo={pagination.showInfo}
							/>
						)}
					</div>
					{picker && (
						<div className="flex-shrink-0 py-2">
							<ColumnLayoutPicker {...picker} onAddColumn={onAddColumn} addColumnLabel={addColumnLabel} />
						</div>
					)}
				</div>
			)}
			{selection?.scopeBanner && <SelectAllInFilterBanner {...selection.scopeBanner} />}
			{/* Bounded so the pagination above stays in view on a long page; inside a flex column
			    (Page scrollMode="content") the remaining height bounds it first. */}
			<Card className="p-0 min-h-0 max-h-[calc(100dvh-10rem)] overflow-auto">
				<table className="w-full text-md text-bgBase-800" style={{ minWidth: minWidth || undefined }}>
					<TableHead
						columns={stickyColumns}
						selection={selection}
						sort={sort}
						scrolls={scrolls}
						tableId={generatedId}
						firstColumnResize={firstColumnResize}
					/>
					<tbody>
						{isLoading ? (
							<StateRow colSpan={colSpan}>Wird geladen …</StateRow>
						) : rows.length === 0 ? (
							<StateRow colSpan={colSpan}>{emptyLabel}</StateRow>
						) : (
							rows.map((row) => {
								const id = getId(row);
								const isExpanded = expansion?.isExpanded(id) ?? false;
								return (
									<Fragment key={id}>
										<TableRow
											row={row}
											rowId={String(id)}
											columns={stickyColumns}
											scrolls={scrolls}
											selection={selection}
											isSelected={selection?.isSelected(id) ?? false}
											isHighlighted={highlightedId === id}
											isExpanded={isExpanded}
											onRowClick={onRowClick}
											rowProps={getRowProps?.(row)}
										/>
										{isExpanded && expansion.renderExpanded(row, colSpan)}
									</Fragment>
								);
							})
						)}
					</tbody>
				</table>
			</Card>

			{/* Zero-height sticky anchor: the bar sits at the scrollport bottom while the table is in view, also
			    on pages that scroll as a whole (an absolute bar would hang off the table's bottom edge, out of view). */}
			{bulkBar && <div className="sticky bottom-0 z-30 h-0">{bulkBar}</div>}
		</div>
	);
};

Table.propTypes = {
	rows: PropTypes.array.isRequired,
	/**
	 * Column config. `render(row)` returns the cell content, so entity-specific
	 * markup stays in the feature. Any sticky-left column other than the first
	 * needs `stickyWidth` so the columns after it can be offset correctly.
	 */
	columns: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.node,
			render: PropTypes.func.isRequired,
			sortable: PropTypes.bool,
			align: PropTypes.oneOf(['left', 'center', 'right']),
			sticky: PropTypes.oneOf(['left', 'right']),
			stickyWidth: PropTypes.number,
			width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			/** Numeric widths add up to the table's minimum width, below which it scrolls horizontally. */
			minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			/** Takes the spare width; its content is clipped to the column, so it should truncate. */
			grow: PropTypes.bool,
			/** Rendered next to the label, e.g. a filter popover trigger. */
			headerAccessory: PropTypes.node,
			headerClassName: PropTypes.string,
			cellClassName: PropTypes.string,
			/** Merged into the cell's inline style, e.g. to lift a cell whose content overflows it. */
			cellStyle: PropTypes.object,
			/** Name in the column picker when `label` is not plain text. */
			layoutLabel: PropTypes.string,
			/** false keeps the column visible and in place (with `layoutKey`). */
			hideable: PropTypes.bool,
			/** Offered in the column picker but hidden until the user shows it. */
			defaultHidden: PropTypes.bool,
			/** Shows a pencil next to the column in the picker. */
			onLayoutEdit: PropTypes.func,
		}),
	).isRequired,
	rowId: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
	selection: PropTypes.shape({
		isSelected: PropTypes.func.isRequired,
		onToggleOne: PropTypes.func.isRequired,
		onToggleAll: PropTypes.func.isRequired,
		allChecked: PropTypes.bool.isRequired,
		someChecked: PropTypes.bool.isRequired,
		/** From useScopedSelection: renders the "Alle N auswählen" banner above the table. */
		scopeBanner: PropTypes.shape(scopeBannerPropTypes),
	}),
	sort: PropTypes.shape({
		sortKey: PropTypes.string,
		sortDir: PropTypes.oneOf(['asc', 'desc']),
		onSortChange: PropTypes.func.isRequired,
	}),
	pagination: PropTypes.shape({
		currentPage: PropTypes.number.isRequired,
		totalPages: PropTypes.number.isRequired,
		onPageChange: PropTypes.func.isRequired,
		totalItems: PropTypes.number,
		pageSize: PropTypes.number,
		showInfo: PropTypes.bool,
	}),
	expansion: PropTypes.shape({
		isExpanded: PropTypes.func.isRequired,
		renderExpanded: PropTypes.func.isRequired,
	}),
	onRowClick: PropTypes.func,
	getRowProps: PropTypes.func,
	highlightedId: PropTypes.string,
	isLoading: PropTypes.bool,
	/** Says why the table is empty; a bare "no results" reads as a glitch. */
	emptyLabel: PropTypes.string,
	/** The first column is frozen by default; false for tables whose first column is not the row identity. */
	firstColumnSticky: PropTypes.bool,
	/**
	 * Row of non-destructive actions above the table. `label` names what they run on
	 */
	actions: PropTypes.shape({
		label: PropTypes.string.isRequired,
		buttons: PropTypes.node.isRequired,
	}),
	bulkBar: PropTypes.node,
	/**
	 * Enables the "Spalten" picker (show/hide, drag-and-drop order), persisted per key in the
	 * user's preferences, as is the dragged width of the first column. Use a stable key per
	 * table, e.g. 'contactListContacts'.
	 */
	layoutKey: PropTypes.string,
	/** Adds a create button at the bottom of the column picker (needs `layoutKey`). */
	onAddColumn: PropTypes.func,
	addColumnLabel: PropTypes.string,
};

export default Table;
